"use client";

import Image from "next/image";
import { Heading } from "@/components/ui/typography";
import Link from "next/link";

export default function ForeignOwners({ heading = "HK Company Bookkeeping for Foreign Owners", resultHeading = "The Result" }: { heading?: string; resultHeading?: string } = {}) {
  return (
    <section className="py-[30px] md:py-[60px] px-4 min-[1340px]:px-0">
      <div className="max-w-[1200px] mx-auto flex flex-col lg:flex-row gap-8 items-center">
        {/* LEFT IMAGE SECTION */}
        <div
          className="w-full lg:basis-1/2 relative min-h-[480px] rounded-2xl overflow-hidden flex-shrink-0"
          style={{
            backgroundImage: "url(/offshore-frame.webp)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          {/* Overlay Card */}
          <div className="absolute bottom-6 left-6 bg-black/80 backdrop-blur-md rounded-xl p-6 flex flex-col gap-4 max-w-[320px]">
            {/* Header */}
            <div className="flex items-center gap-4">
              <div className="bg-black rounded-full p-2">
                <svg
                  width="17"
                  height="14"
                  viewBox="0 0 17 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6.3654 5.71602C6.90944 5.89815 7.49066 5.9905 8.09297 5.9905H10.3332C10.5285 5.99047 10.7225 6.02306 10.9071 6.08692C11.5348 5.44034 11.9219 4.55902 11.9219 3.58887C11.9219 1.60996 10.3119 0 8.33301 0C6.3541 0 4.74414 1.60996 4.74414 3.58887C4.74414 4.28005 4.94072 4.92608 5.28066 5.47435C5.65508 5.51902 6.01797 5.59971 6.3654 5.71602ZM7.98441 4.76322C7.92028 4.76331 7.85676 4.75072 7.79751 4.72617C7.73826 4.70163 7.68444 4.66561 7.63916 4.62019L6.96429 3.94535C6.7736 3.75469 6.7736 3.44554 6.96429 3.25482C7.15495 3.06416 7.46413 3.0641 7.65482 3.25482L7.98444 3.58441L9.0114 2.55746C9.20206 2.3668 9.51124 2.3668 9.70193 2.55746C9.89262 2.74812 9.89262 3.0573 9.70193 3.24799L8.32972 4.62019C8.28442 4.66559 8.23059 4.7016 8.17133 4.72615C8.11208 4.75069 8.04855 4.76329 7.98441 4.76322Z"
                    fill="white"
                  />
                  <path
                    d="M10.3335 9.51708H6.85623C6.58656 9.51708 6.36794 9.29846 6.36794 9.0288C6.36794 8.75914 6.58656 8.54052 6.85623 8.54052H10.3335C10.7671 8.54052 11.1199 8.18772 11.1199 7.75409C11.1199 7.32047 10.7671 6.96767 10.3335 6.96767H8.09327C7.38536 6.96767 6.69981 6.85836 6.0557 6.64273C5.59447 6.48834 5.09711 6.41008 4.57738 6.41008C4.06426 6.41008 3.60899 6.48707 3.24473 6.58049V12.0783H9.75551C10.3392 12.0783 10.905 11.8763 11.3567 11.5065L16.232 7.51558C16.7321 7.10692 16.8113 6.37401 16.4141 5.86832C16.1831 5.57427 15.8364 5.42401 15.4859 5.42401C15.2144 5.42401 14.9406 5.51412 14.7163 5.69738L12.0943 7.83957C12.0496 8.77209 11.2769 9.51708 10.3335 9.51708ZM1.77302 6.2343H0.488282C0.21862 6.2343 0 6.45292 0 6.72258V12.8372C0 13.1069 0.21862 13.3255 0.488282 13.3255H1.77302C2.04268 13.3255 2.2613 13.1069 2.2613 12.8372V6.72258C2.2613 6.45292 2.04268 6.2343 1.77302 6.2343Z"
                    fill="white"
                  />
                </svg>
              </div>
              <h3 className="text-white text-[20px] font-semibold">
                {resultHeading}
              </h3>
            </div>

            {/* List */}
            <div className="border-l-2 border-white/10 pl-4 flex flex-col gap-3">
              {[
                "Stay compliant without effort",
                "Manage compliance remotely",
                "Prepare audit-ready accounts continuously",
              ].map((text, i) => (
                <div key={i} className="flex items-start gap-2">
                  <Image
                    src="/black-tick.svg"
                    alt="check"
                    width={19}
                    height={19}
                    className="mt-[4px] [filter:brightness(0)_invert(1)]"
                  />
                  <p className="text-[14px] text-white/80 leading-[160%]">
                    {text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT CONTENT */}
        <div className="w-full lg:basis-1/2 flex flex-col gap-8">
          <div className="flex flex-col gap-6">
            <Heading as="h2" variant="section">
              {heading}
            </Heading>

            <p className="text-white/70 text-[16px] leading-[24px]">
              If you operate internationally, compliance becomes harder because
              your transactions span currencies, payment processors operate
              globally, suppliers are overseas, and management decisions happen
              remotely.
            </p>
          </div>

          <a
            href="https://calendly.com/d/cxy2-dy2-tqk/mutetaxes-consultation-call"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-[#335cff] text-white px-4 py-2 rounded-lg w-fit"
          >
            <span className="text-[14px] font-medium">
              Book a free consultation
            </span>
            <Image src="/button-arrow.svg" alt="" width={18} height={18} />
          </a>

          <p className="text-white text-[16px] leading-[24px]">
            MuteTaxes structures your bookkeeping so your company remains
            compliant with{" "}
            <Link
              href="/bookkeeping-services-hong-kong"
              className="hover:underline"
            >
              Hong Kong filing standards
            </Link>{" "}
            even when operating globally.
          </p>
        </div>
      </div>
    </section>
  );
}
